import { useState, useRef, useEffect } from 'react';
import { faqs } from '../data/faq';

interface Props {
  onContactClick: () => void;
}

interface FAQItemProps {
  q: string;
  a: string;
  isOpen: boolean;
  onToggle: () => void;
}

const FAQItem = ({ q, a, isOpen, onToggle }: FAQItemProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div
      className={`overflow-hidden rounded-2xl border transition-[background-color,border-color] duration-[250ms] ${
        isOpen ? 'border-ak-sky/25 bg-ak-dark' : 'border-white/5 bg-ak-dark/50'
      }`}
    >
      <button
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center justify-between gap-4 border-0 bg-transparent px-[22px] py-[18px] text-left font-sans"
      >
        <span className={`flex-1 text-[15px] font-semibold leading-[1.4] ${isOpen ? 'text-ak-sky' : 'text-white'}`}>
          {q}
        </span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
            isOpen ? 'bg-ak-sky/15' : 'bg-white/[0.06]'
          }`}
        >
          <i className={`pi pi-${isOpen ? 'minus' : 'plus'} text-[11px] ${isOpen ? 'text-ak-sky' : 'text-ak-dim'}`}></i>
        </span>
      </button>

      <div ref={contentRef} className="overflow-hidden transition-[max-height] duration-[350ms]" style={{ maxHeight: height }}>
        <p className="m-0 px-[22px] pb-5 text-sm leading-[1.7] text-ak-muted">
          {a}
        </p>
      </div>
    </div>
  );
};

const FAQ = ({ onContactClick }: Props) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="border-t border-white/5 px-8 py-[clamp(48px,8vw,80px)]">
      <div className="mx-auto max-w-[760px]">
        <div className="mb-12 text-center">
          <h2 className="mb-3 text-[clamp(1.5rem,3vw,2.25rem)] font-bold text-white">
            Sie haben Fragen? Ich habe Antworten.
          </h2>
          <p className="m-0 text-[15px] text-ak-muted">
            Das fragen mich die meisten Kunden – vielleicht hilft das auch Ihnen weiter.
          </p>
        </div>

        <div className="flex flex-col gap-2.5">
          {faqs.map((item, i) => (
            <FAQItem
              key={i}
              q={item.q}
              a={item.a}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 rounded-[20px] border border-ak-sky/15 bg-ak-dark/50 px-6 py-7 text-center">
          <p className="mb-4 text-[15px] text-ak-muted">
            Ihre Frage ist nicht dabei? Schreiben Sie mir einfach – ich antworte persönlich.
          </p>
          <button onClick={onContactClick} className="btn-primary text-sm">
            <i className="pi pi-envelope"></i> Kontakt aufnehmen
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
